import { jsx as _jsx } from "react/jsx-runtime";
/**
 * useActionBar — partage le slot CTA bottom safe-area pour les games (Sprint 2.2 L3).
 *
 * Pattern symétrique à l onboarding : un provider expose deux nodes (primary +
 * secondary) que les phases (Intro, Debrief, ...) peuvent setter via
 * `useActionBar({ primary, secondary })`. La barre sticky est rendue par
 * <ActionBarSlot /> en bas du shell, padding env(safe-area-inset-bottom)
 * pour rester tappable au-dessus du home indicator iOS.
 *
 * Usage minimal d un jeu :
 *
 *   <ActionBarProvider>
 *     <GameShell config={...} adapter={...} ...>
 *       <PhaseRenderer
 *         intro={<IntroSlot />}     // dedans : useActionBar({ primary, secondary })
 *         debrief={<DebriefSlot />} // idem
 *       />
 *     </GameShell>
 *     <ActionBarSlot />
 *   </ActionBarProvider>
 *
 * Le provider est volontairement séparé de GameShell pour rester opt-in :
 * un jeu qui ne veut pas du pattern n est pas obligé de l adopter.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
const Ctx = createContext(null);
export function ActionBarProvider({ children }) {
    const [primary, setPrimary] = useState(null);
    const [secondary, setSecondary] = useState(null);
    const setActions = useCallback((next) => {
        if ('primary' in next)
            setPrimary(next.primary ?? null);
        if ('secondary' in next)
            setSecondary(next.secondary ?? null);
    }, []);
    const clearActions = useCallback(() => {
        setPrimary(null);
        setSecondary(null);
    }, []);
    const value = useMemo(() => ({ primary, secondary, setActions, clearActions }), [primary, secondary, setActions, clearActions]);
    return _jsx(Ctx.Provider, { value: value, children: children });
}
/**
 * Hook côté phase : pousse une paire (primary + secondary) dans la sticky
 * bottom ActionBar et nettoie au unmount.
 *
 * Pattern :
 *   useActionBar({
 *     primary: <button>Démarrer la session</button>,
 *     secondary: <button>Voir un exemple</button>,
 *   });
 *
 * L identité React des nodes peut changer à chaque render (JSX inline) :
 * on ne re-set qu une fois au mount + à chaque changement réel des deps
 * caller-controlled. Pour réagir aux changements de state (ex disabled,
 * label conditionnel), le caller doit utiliser la version `setActions`
 * manuelle exposée par `useActionBarApi()` avec son propre useEffect.
 *
 * Pour la majorité des intros / debriefs où les boutons ne changent pas
 * en cours de phase, ce hook auto-mount suffit.
 */
export function useActionBar(actions) {
    const ctx = useContext(Ctx);
    useEffect(() => {
        if (!ctx)
            return;
        ctx.setActions({
            primary: actions?.primary ?? null,
            secondary: actions?.secondary ?? null,
        });
        return () => ctx.clearActions();
        // Volontairement vide : on set au mount, on clear au unmount.
        // Pour des CTA dynamiques (disabled state etc.), utiliser useActionBarApi.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return useMemo(() => ({
        setActions: ctx?.setActions ?? (() => undefined),
        clearActions: ctx?.clearActions ?? (() => undefined),
    }), [ctx]);
}
/**
 * Hook bas niveau pour les phases qui doivent re-set leur ActionBar quand
 * l état interne change (disabled, loading, label conditionnel).
 *
 * Usage :
 *   const { setActions } = useActionBarApi();
 *   useEffect(() => {
 *     setActions({ primary: <button disabled={!canSubmit}>...</button> });
 *   }, [canSubmit]);
 */
export function useActionBarApi() {
    const ctx = useContext(Ctx);
    return useMemo(() => ({
        setActions: ctx?.setActions ?? (() => undefined),
        clearActions: ctx?.clearActions ?? (() => undefined),
    }), [ctx]);
}
/**
 * Hook lecture seule pour <ActionBarSlot /> qui rend la barre.
 */
export function useActionBarSnapshot() {
    const ctx = useContext(Ctx);
    if (!ctx)
        return { primary: null, secondary: null };
    return { primary: ctx.primary, secondary: ctx.secondary };
}
//# sourceMappingURL=useActionBar.js.map